import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { motion } from 'motion/react';

import { cn, varHover, motionTransition } from '../../utils';

// Props that conflict between React's ButtonHTMLAttributes and motion's HTMLMotionProps
type ConflictingProps =
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd'
  | 'onAnimationStart'
  | 'onAnimationEnd';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  ConflictingProps
> {
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isActive?: boolean;
  badge?: number | string;
  /** Enable hover/tap scale animation */
  animated?: boolean;
  children: ReactNode;
}

const sizeStyles = {
  xs: 'p-1',
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-3',
};

const variantStyles = {
  default: 'bg-tkf-surface-muted text-tkf-fg hover:bg-tkf-line-light',
  ghost: 'bg-transparent text-tkf-fg-muted hover:bg-tkf-surface-muted',
  // bg-transparent (no bg-white) so the button stays visible on white cards —
  // the border is the container; the parent surface shows through. On hover
  // the whole control (border + bg tint + icon) turns teal for clear feedback.
  outline:
    'border border-tkf-line-medium bg-transparent text-tkf-fg hover:border-primary-500 hover:bg-primary-alpha-10 hover:text-primary-500',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = 'ghost',
      size = 'md',
      isActive = false,
      badge,
      animated = false,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const buttonClasses = cn(
      'relative inline-flex items-center justify-center rounded-full transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
      sizeStyles[size],
      variantStyles[variant],
      isActive && 'bg-primary-50 text-primary-700',
      disabled && 'cursor-not-allowed opacity-50',
      className
    );

    const badgeElement = badge !== undefined && (
      <span
        className={cn(
          'absolute -right-1 -top-1 flex items-center justify-center',
          'min-w-[18px] h-[18px] px-1 rounded-full',
          'bg-error-500 text-white text-[10px] font-semibold'
        )}
      >
        {typeof badge === 'number' && badge > 99 ? '99+' : badge}
      </span>
    );

    // Animated version with motion.button
    if (animated && !disabled) {
      return (
        <motion.button
          ref={ref}
          type="button"
          className={buttonClasses}
          disabled={disabled}
          whileHover="hover"
          whileTap="tap"
          variants={varHover(1.05)}
          transition={motionTransition.hover}
          {...props}
        >
          {children}
          {badgeElement}
        </motion.button>
      );
    }

    // Standard button (default behavior)
    return (
      <button ref={ref} type="button" className={buttonClasses} disabled={disabled} {...props}>
        {children}
        {badgeElement}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
